import React, { useEffect, useState } from 'react';
import TableItem, {
  ITableItemSkillLevel,
} from '@components/progress/table/TableItem';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { Icon } from 'geniebook-ui/dist';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import TableItemV2 from '@components/progress/table/TableItemV2';
import { useAtom } from 'jotai';
import { selectedBreakdownIdAtom } from '../ProgressStore';
import { useParams } from 'react-router-dom';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/SubjectParser';

export interface TableSkillLevelProps {
  list: ITableItemSkillLevel[];
  onCheckboxClick: (id: number | string, newStatus: boolean) => void;
  haveMasteryLevel: boolean;
  onSortSkillLevel: (sortBy: number | string) => void;
  sortBySkillLevel: number | string;
  hasSelectedItems: boolean;
}

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const TableSkillLevel = (props: TableSkillLevelProps) => {
  const [selectedBreakdownId] = useAtom(selectedBreakdownIdAtom);
  const [tableHeight, setTableHeight] = useState(500);
  const width = useViewWidth();
  const { subjectId } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: Number(subjectIdFromName(subjectId)),
  });
  const newProgressIsOn =
    useFeatureIsOn('student_new_progress_page') || Boolean(isJC);
  const [defaultTableHeight, setDefaultTableHeight] = useState(0);

  const showSkillLevel = props.haveMasteryLevel || Boolean(isJC);
  function calculateTableHeight() {
    const element = document.getElementById('tableSkillLevel') as HTMLElement;
    const informationElement = document.getElementById(
      'information-skill-level',
    ) as HTMLElement;
    const startY = element.getBoundingClientRect().top;
    const informationHeight = informationElement
      ? informationElement.getBoundingClientRect().height
      : 0;
    const viewHeight = window.innerHeight;
    const offset = window.innerWidth < 1024 ? 120 : 0;

    setTableHeight(
      Math.max(informationHeight - 45 - 4, viewHeight - startY - offset),
    );

    // this is the default table height
    if (defaultTableHeight == 0)
      setDefaultTableHeight(viewHeight - startY - offset);
  }

  useEffect(() => {
    if (
      !selectedBreakdownId &&
      defaultTableHeight > 0 &&
      width >= breakpoint.lg
    ) {
      setTableHeight(defaultTableHeight);
      return;
    }

    calculateTableHeight();
  }, [props.hasSelectedItems, selectedBreakdownId]);

  return (
    <div className={'text-neutral-whiteboard w-full text-left'}>
      <div
        className={
          'py-3 px-4 rounded-2xl bg-primary-navyblue-shade/[0.65] flex gap-4 font-bold text-sm max-lg:text-xs'
        }
      >
        <div className={'flex-auto'}>Topic</div>
        <div className={'flex-none text-center w-20 max-md:hidden'}>Level</div>
        <div
          className={`flex-none text-right ${
            showSkillLevel ? 'w-24' : 'w-20'
          } cursor-pointer`}
          onClick={() => {
            props.onSortSkillLevel(
              props.sortBySkillLevel === 'weakest' ? 'strongest' : 'weakest',
            );
          }}
        >
          {showSkillLevel ? 'Skill Level' : 'Score'}{' '}
          <Icon
            name={
              props.sortBySkillLevel === 'weakest' ? 'arrow-down' : 'arrow-up'
            }
            size={'1x'}
            className={'text-primary-apricot ml-1'}
          />
        </div>
      </div>
      <div
        className={'overflow-auto no-scrollbar'}
        style={{
          maxHeight: width < breakpoint.lg ? 'unset' : tableHeight + 'px',
        }}
        id={'tableSkillLevel'}
        data-testid={'table-skill-level'}
      >
        {props.list.map((item, index) => {
          return newProgressIsOn ? (
            <TableItemV2
              {...item}
              onCheckboxClick={props.onCheckboxClick}
              key={item.id + '_' + index}
            />
          ) : (
            <TableItem
              {...item}
              onCheckboxClick={props.onCheckboxClick}
              key={item.id + '_' + index}
              haveMasterylevel={showSkillLevel}
            />
          );
        })}
        {/* commented because there's ProgressUpsellingBanner */}
        {props.hasSelectedItems && width >= breakpoint.lg && (
          <div className={'lg:h-[100px]'}></div>
        )}
      </div>
    </div>
  );
};
export default TableSkillLevel;
